import React from 'react';
import { useDrag, useDrop } from 'react-dnd';

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

function QuarterlyView({ items, onItemClick, onItemUpdate }) {
  const getItemsByQuarter = (quarter) => {
    return items.filter((item) => item.timeframe === quarter);
  };

  const handleDrop = async (item, newQuarter) => {
    if (item.timeframe !== newQuarter) {
      try {
        await onItemUpdate({ ...item, timeframe: newQuarter });
      } catch (error) {
        console.error('Failed to update item:', error);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="quarterly-view">
      {QUARTERS.map((quarter) => (
        <QuarterColumn
          key={quarter}
          quarter={quarter}
          items={getItemsByQuarter(quarter)}
          onItemClick={onItemClick}
          onDrop={handleDrop}
        />
      ))}
    </div>
  );
}

function QuarterColumn({ quarter, items, onItemClick, onDrop }) {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'roadmap-item',
      drop: (item) => {
        onDrop(item, quarter);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [quarter, onDrop]
  );

  return (
    <div
      ref={drop}
      className={`bg-card border-2 rounded-xl p-4 min-h-[600px] transition-all ${
        isOver ? 'border-primary bg-accent/5 shadow-lg' : 'border-border'
      }`}
      data-testid={`quarter-column-${quarter}`}
    >
      <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-border">
        <h2 className="text-xl font-bold">{quarter} 2026</h2>
        <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-md">
          {items.length}
        </span>
      </div>
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">No items in {quarter}</p>
            <p className="text-xs mt-1">Drag items here</p>
          </div>
        ) : (
          items.map((item) => (
            <DraggableItem
              key={item._id}
              item={item}
              onClick={() => onItemClick(item)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DraggableItem({ item, onClick }) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'roadmap-item',
      item: item,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [item]
  );

  return (
    <div
      ref={drag}
      onClick={onClick}
      className={`bg-background border border-border rounded-lg p-3 cursor-move hover:shadow-md hover:border-primary transition-all ${
        isDragging ? 'opacity-30' : 'opacity-100'
      }`}
      data-testid={`quarter-item-${item._id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded uppercase ${
            item.type === 'initiative'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : item.type === 'epic'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
          }`}
        >
          {item.type}
        </span>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded capitalize ${
            item.status === 'planned'
              ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              : item.status === 'in-progress'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : item.status === 'done'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
          }`}
        >
          {item.status}
        </span>
      </div>
      <h3 className="font-semibold mb-1 text-sm">{item.title}</h3>
      {item.description && (
        <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
      )}
      {(item.rice_score || item.wsjf_score) && (
        <div className="mt-2 flex gap-2">
          {item.rice_score && (
            <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-0.5 rounded">
              RICE: {item.rice_score.toFixed(1)}
            </span>
          )}
          {item.wsjf_score && (
            <span className="text-xs font-mono bg-secondary/10 text-secondary-foreground px-2 py-0.5 rounded">
              WSJF: {item.wsjf_score.toFixed(1)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default QuarterlyView;
